/**
 * Phase 10d — Historique des rappels d'échéance envoyés pour une facture fournisseur.
 *
 * Affiche `invoice.reminders_log` (rempli par `supplier_invoice_reminders_service.py`) sous
 * forme de mini timeline verticale. N'apparaît que si au moins 1 rappel a été envoyé.
 *
 * Types possibles :
 *   - "j5"      : rappel J-5 (avant échéance)
 *   - "j1"      : rappel J-1 (veille d'échéance)
 *   - "overdue" : rappel en retard (peut se répéter chaque jour)
 *
 * Chaque entrée = { type, sent_at (ISO), digest_id }.
 * Rappel : ces emails sont internes (admins + secrétaires), aucune donnée de marge exposée.
 */
import { BellRing, Clock, AlertTriangle } from "lucide-react";

const TYPE_META = {
  j5: {
    label: "Rappel J-5 (avant échéance)",
    icon: BellRing,
    cls: "border-slate-700 bg-slate-800/50 text-slate-300",
  },
  j1: {
    label: "Rappel J-1 (veille d'échéance)",
    icon: Clock,
    cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  },
  overdue: {
    label: "Rappel en retard",
    icon: AlertTriangle,
    cls: "border-red-500/40 bg-red-500/10 text-red-300",
  },
};

function shortDateTimeBE(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const date = dt.toLocaleDateString("fr-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const h = String(dt.getHours()).padStart(2, "0");
  const m = String(dt.getMinutes()).padStart(2, "0");
  return `${date} · ${h}h${m}`;
}

export default function SupplierInvoiceRemindersHistory({ invoice }) {
  const log = Array.isArray(invoice?.reminders_log) ? invoice.reminders_log : [];
  if (log.length === 0) return null;

  // Trier du plus récent au plus ancien
  const sorted = [...log].sort((a, b) => {
    const ta = new Date(a?.sent_at || 0).getTime();
    const tb = new Date(b?.sent_at || 0).getTime();
    return tb - ta;
  });

  return (
    <div
      className="rounded border border-slate-800 bg-slate-950/40 p-3"
      data-testid="supplier-invoice-reminders-history"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Historique rappels ({sorted.length})
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">
          Interne · équipe uniquement
        </span>
      </div>
      <ol className="space-y-2">
        {sorted.map((entry, i) => {
          const meta = TYPE_META[entry?.type] || TYPE_META.overdue;
          const Icon = meta.icon;
          return (
            <li
              key={`${entry?.digest_id || "d"}-${entry?.type}-${i}`}
              className="flex items-start gap-2"
              data-testid={`reminder-log-entry-${entry?.type || "unknown"}`}
            >
              <div
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${meta.cls}`}
              >
                <Icon className="h-3 w-3" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-100">{meta.label}</div>
                <div className="text-xs text-slate-500">
                  Envoyé le {shortDateTimeBE(entry?.sent_at)}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
